import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import useBoardStore from '../../../store/boardStore';

type ImportSource = 'dotstorming' | 'txtcsv';

type ImportCardsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const ImportCardsDialog = ({ open, onClose }: ImportCardsDialogProps) => {
  const actionError = useBoardStore((state) => state.actionError);
  const clearActionError = useBoardStore((state) => state.clearActionError);
  const getImportableDotstormingBoards = useBoardStore(
    (state) => state.getImportableDotstormingBoards
  );
  const getImportableDotstormingCards = useBoardStore(
    (state) => state.getImportableDotstormingCards
  );
  const importTxtCsvCards = useBoardStore((state) => state.importTxtCsvCards);
  const importDotstormingCards = useBoardStore((state) => state.importDotstormingCards);

  const [source, setSource] = useState<ImportSource>('dotstorming');
  const [boards, setBoards] = useState<Array<{ id: string; title: string }>>([]);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [cards, setCards] = useState<
    Array<{ id: string; title?: string; content?: string; voteCount?: number }>
  >([]);
  const [importAllCards, setImportAllCards] = useState(true);
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const cardsByVotes = useMemo(
    () =>
      [...cards].sort((left, right) => {
        const voteDiff = (right.voteCount ?? 0) - (left.voteCount ?? 0);
        if (voteDiff !== 0) {
          return voteDiff;
        }

        const leftLabel = (left.title?.trim() || left.content?.trim() || '').toLowerCase();
        const rightLabel = (right.title?.trim() || right.content?.trim() || '').toLowerCase();
        return leftLabel.localeCompare(rightLabel);
      }),
    [cards]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setSource('dotstorming');
    setBoards([]);
    setSelectedBoardId('');
    setCards([]);
    setImportAllCards(true);
    setSelectedCardIds([]);
    setSelectedFile(null);
    setIsLoadingBoards(false);
    setIsLoadingCards(false);
    setIsImporting(false);
    setLocalError(null);
    clearActionError();
  }, [clearActionError, open]);

  useEffect(() => {
    if (!open || source !== 'dotstorming') {
      return;
    }

    setIsLoadingBoards(true);
    void (async () => {
      const nextBoards = await getImportableDotstormingBoards();
      setBoards(nextBoards);
      setSelectedBoardId((currentBoardId) => {
        if (currentBoardId && nextBoards.some((board) => board.id === currentBoardId)) {
          return currentBoardId;
        }

        return nextBoards[0]?.id ?? '';
      });
      setIsLoadingBoards(false);
    })();
  }, [getImportableDotstormingBoards, open, source]);

  useEffect(() => {
    if (!open || source !== 'dotstorming' || !selectedBoardId) {
      return;
    }

    setIsLoadingCards(true);
    setCards([]);
    setSelectedCardIds([]);

    void (async () => {
      const nextCards = await getImportableDotstormingCards(selectedBoardId);
      setCards(nextCards);
      setIsLoadingCards(false);
    })();
  }, [getImportableDotstormingCards, open, selectedBoardId, source]);

  const toggleCardSelection = (cardId: string) => {
    setSelectedCardIds((currentCardIds) =>
      currentCardIds.includes(cardId)
        ? currentCardIds.filter((currentCardId) => currentCardId !== cardId)
        : [...currentCardIds, cardId]
    );
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setLocalError(null);
  };

  const handleImport = async () => {
    if (isImporting) {
      return;
    }

    setLocalError(null);
    clearActionError();
    setIsImporting(true);

    try {
      if (source === 'txtcsv') {
        if (!selectedFile) {
          setLocalError('Please select a .txt or .csv file.');
          return;
        }

        const didImport = await importTxtCsvCards(selectedFile);
        if (didImport) {
          onClose();
        }

        return;
      }

      if (!selectedBoardId) {
        setLocalError('Please select a source board.');
        return;
      }

      if (!importAllCards && selectedCardIds.length === 0) {
        setLocalError('Select at least one card or choose "Import all cards".');
        return;
      }

      const didImport = await importDotstormingCards(
        selectedBoardId,
        importAllCards ? undefined : selectedCardIds
      );

      if (didImport) {
        onClose();
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isImporting && onClose()} fullWidth maxWidth="md">
      <DialogTitle>Import Cards</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Tabs
            value={source}
            onChange={(_, nextSource: ImportSource) => {
              setSource(nextSource);
              setLocalError(null);
              clearActionError();
            }}
          >
            <Tab value="dotstorming" label="My dotstorming board" />
            <Tab value="txtcsv" label=".TXT or .CSV file" />
          </Tabs>

          {source === 'dotstorming' && (
            <Stack spacing={2}>
              <TextField
                select
                label="Source board"
                value={selectedBoardId}
                onChange={(event) => setSelectedBoardId(event.target.value)}
                disabled={isLoadingBoards || boards.length === 0}
                fullWidth
              >
                {boards.map((board) => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.title}
                  </MenuItem>
                ))}
              </TextField>

              {isLoadingBoards && <Typography variant="body2">Loading boards...</Typography>}
              {!isLoadingBoards && boards.length === 0 && (
                <Typography variant="body2">No boards available to import from.</Typography>
              )}

              {boards.length > 0 && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={importAllCards}
                        onChange={(event) => setImportAllCards(event.target.checked)}
                      />
                    }
                    label="Import all cards"
                  />
                  {!importAllCards && (
                    <Box
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: 280,
                        overflow: 'auto',
                      }}
                    >
                      {isLoadingCards ? (
                        <Box sx={{ p: 2 }}>
                          <Typography variant="body2">Loading cards...</Typography>
                        </Box>
                      ) : cardsByVotes.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                          <Typography variant="body2">No cards available to import.</Typography>
                        </Box>
                      ) : (
                        <List dense disablePadding>
                          {cardsByVotes.map((card) => {
                            const label =
                              card.title?.trim() || card.content?.trim() || '(Untitled card)';
                            const secondaryText =
                              typeof card.voteCount === 'number'
                                ? `${card.voteCount} ${card.voteCount === 1 ? 'vote' : 'votes'}`
                                : undefined;

                            return (
                              <ListItem key={card.id} disablePadding>
                                <ListItemButton onClick={() => toggleCardSelection(card.id)} dense>
                                  <Checkbox
                                    edge="start"
                                    checked={selectedCardIds.includes(card.id)}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                  <ListItemText primary={label} secondary={secondaryText} />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}

          {source === 'txtcsv' && (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                Upload a plain text or CSV file. Each line or CSV cell will be imported as a text
                card.
              </Typography>
              <Button variant="outlined" component="label" disabled={isImporting}>
                {selectedFile ? 'Change file' : 'Select .txt/.csv file'}
                <input
                  type="file"
                  hidden
                  accept=".txt,.csv,text/plain,text/csv,application/vnd.ms-excel"
                  onChange={handleFileChange}
                />
              </Button>
              {selectedFile && <Typography variant="body2">{selectedFile.name}</Typography>}
            </Stack>
          )}

          {localError ? <Alert severity="error">{localError}</Alert> : null}
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isImporting}>
          Cancel
        </Button>
        <Button onClick={() => void handleImport()} variant="contained" disabled={isImporting}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportCardsDialog;
